import { useContext, useEffect, useRef } from "react";
import { useParams, usePathname } from "next/navigation";
import { FormErrors, UseFormReturnType } from "@mantine/form";
import { equals, mapValues } from "remeda";
import type { Contract } from "@/types/contract";
import type { Product, Process as ProcessEnumeration } from "@/types/enumerations";
import { useGetContractQueryData } from "@/hooks/useGetContractQueryData";
import { EnumerationsContext } from "@/contexts/EnumerationsContext";
import { useQueryStorage } from "@/contexts/QueryStorageContext";
import type { ParsedQueryParams } from "@/screens/Welcome/useQueryParams";
import {
  buildCustomerSubType,
  buildCustomerType,
  buildProcess,
  buildProduct,
  buildProductType,
  pushFormError,
  pushFormLoad,
  pushFormStart,
  pushFormStepNext,
  pushFormStepPrevious,
  pushFormSubmitSuccess,
  pushFormUpdate,
  resolveFormStep,
  type FormData,
} from "@/utils/dataLayer";

interface FormStepLoadConfig {
  name: string;
  form: {
    getValues?: () => unknown;
  };
}

type FormDataEnums = {
  products?: Product[];
  processes?: ProcessEnumeration[];
};

interface BuildFormDataParams {
  contract: Contract | undefined;
  uuid: string | undefined;
  pathname: string;
  query: ParsedQueryParams;
  formValues?: Record<string, unknown>;
  formStepId?: string;
  enums?: FormDataEnums;
}

/**
 * Builders prefer the contract (WEB_API) and fall back to useParsedQueryParams'
 * resolved output (persisted in QueryStorageContext) for steps where the contract
 * does not exist yet.
 */
function buildFormData({ contract, uuid, pathname, query, formValues, formStepId, enums }: BuildFormDataParams): FormData {
  const processValue = contract?.process ?? query.process?.value;
  return {
    formStep: resolveFormStep(pathname),
    formStepId,
    formType: "eContracts FE" as const,
    productType: buildProductType(contract?.supplyPoint.commodity ?? query.commodity?.value),
    product: buildProduct(contract, enums?.products) ?? query.product?.name,
    customerType: buildCustomerType(contract?.segment, query.customerType),
    customerSubType: buildCustomerSubType(contract?.segment, contract?.customer.companyType, query.customerType),
    contractNumber: contract?.contractNumber,
    contractId: contract ? String(contract.id) : undefined,
    process: buildProcess(processValue, enums?.processes),
    processValue: processValue ?? undefined,
    processReason: contract?.processReason ?? query.processReason ?? undefined,
    contractUUID: uuid,
    source: query.source ?? contract?.contractSource ?? undefined,
    greenProduct: contract?.greenProduct ?? query.greenProduct,
    formFields: formValues ? (toFilledFlags(formValues) as Record<string, unknown>) : undefined,
  };
}

/**
 * Converts Mantine's `FormErrors` map ({ field: message }) into the data layer's
 * `formFieldErrors` shape: an array of single-key objects (§ 3.1.2.2). Non-string
 * error nodes (rare) fall back to a generic `INVALID` code.
 */
function buildFormFieldErrors(errors: Record<string, unknown>): Array<Record<string, string>> {
  return Object.entries(errors).map(([field, message]) => ({
    [field]: typeof message === "string" ? message : "INVALID",
  }));
}

/**
 * Recursively walks form values and replaces every leaf value with a boolean
 * flag indicating whether it is filled-in (`true`) or empty (`false`), per
 * § 3.1.2.1: `formFields` must carry "only a boolean flag indicating filled-in
 * and empty fields" — never real values. Deny-by-default: no field value is
 * ever emitted, so fields added later (or PII we didn't anticipate) cannot leak.
 * Structure is preserved; nested objects/arrays (e.g. `contact`,
 * `companyDetails`, `executivePeople`, `addresses`) keep their shape with
 * boolean leaves. Non-PII facts (product, customerType, process, …) live as
 * top-level FormData attributes and are unaffected by this.
 */
function toFilledFlags(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(toFilledFlags);
  }
  if (isPlainObject(value)) {
    return mapValues(value, toFilledFlags);
  }
  return !isEmptyValue(value);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" && value !== null && !Array.isArray(value) && Object.getPrototypeOf(value) === Object.prototype
  );
}

function isEmptyValue(value: unknown): boolean {
  return value === undefined || value === null || value === "" || value === false || (Array.isArray(value) && value.length === 0);
}

interface UseFormAnalyticsOptions<T> {
  /** Mantine form instance — enables the formStart / formUpdate field-tracking events. */
  form?: UseFormReturnType<T>;
  /** Identifies the step in emitted events; defaults to `step` when omitted. */
  formStepId?: string;
  /** Open accordion step name — enables the formLoad event. */
  step?: string | null;
  /** Visible accordion forms, used to read field values on formLoad. */
  visibleForms?: FormStepLoadConfig[];
}

// Lives at module scope so it survives component remounts (Suspense, StrictMode),
// which reset component-local refs and would otherwise re-fire formLoad for the same view.
let lastFormLoadKey: string | null = null;

/**
 * formStart / formUpdate: field-level tracking, enabled when a form is given.
 * Lifted into its own hook so its branching is encapsulated and the refs that
 * track the baseline / started state stay local to this concern.
 */
function useFieldTracking<T>(form: UseFormReturnType<T> | undefined, build: (formValues?: Record<string, unknown>) => FormData) {
  const prevValues = useRef<unknown>(undefined);
  const prevFields = useRef<FormData["formFields"]>(undefined);
  const started = useRef(false);
  useEffect(() => {
    if (!form) {
      return;
    }

    // Mantine keeps `values` referentially stable until a field actually
    // changes, so this guard skips the build on re-renders caused by unrelated
    // state — no work happens unless the user touched the form.
    const values = form.values;
    if (values === prevValues.current) {
      return;
    }
    const isBaseline = prevValues.current === undefined;
    prevValues.current = values;

    const formData = build(values as Record<string, unknown>);
    const fields = formData.formFields ?? {};

    // Establish the baseline on first render without emitting an event.
    if (isBaseline) {
      prevFields.current = fields;
      return;
    }

    // Compare the built payload, not raw values: only the filled/empty flags are
    // ever emitted, so a keystroke that leaves every field's filled state
    // untouched (e.g. "ab" -> "abc") produces an identical payload and is a no-op.
    const updatedFormFields = Object.keys(fields).filter(key => !equals(fields[key], prevFields.current?.[key]));
    prevFields.current = fields;

    if (updatedFormFields.length === 0) {
      return;
    }

    // formStart: first payload-changing field on this form (once per step).
    if (!started.current) {
      started.current = true;
      pushFormStart(formData);
    }

    // formUpdate: any change to the emitted field flags.
    // formFieldsValidation (§ 3.1.2.3) is scoped to the changed fields: KO if any
    // updated field currently has a validation error, otherwise OK. Error keys are
    // dot-paths (e.g. "contact.email"), so a top-level updated field matches its
    // nested errors too. The spec's third state, N/A, has no detectable trigger —
    // Mantine can't tell a field that passed validation from one never validated —
    // so it is intentionally not emitted.
    const errorKeys = Object.keys(form.errors);
    const hasError = updatedFormFields.some(field => errorKeys.some(key => key === field || key.startsWith(`${field}.`)));
    pushFormUpdate({
      ...formData,
      updatedFormFields,
      formFieldsValidation: hasError ? "KO" : "OK",
    });
  });
}

/**
 * Single entry point for the form data layer events. Depending on the options
 * provided it wires up formLoad (accordion level: `step` + `visibleForms`) and
 * formStart / formUpdate (form level: `form`), and always returns the
 * step-navigation and error emitters with the FormData payload built internally.
 */
export const useFormAnalytics = <T>({ form, formStepId, step, visibleForms }: UseFormAnalyticsOptions<T> = {}) => {
  const pathname = usePathname();
  const params = useParams();
  const contractState = useGetContractQueryData();
  const { products, processes } = useContext(EnumerationsContext);
  const { storedQueries } = useQueryStorage();

  const stepId = formStepId ?? step ?? undefined;
  const uuid = typeof params.id === "string" ? params.id : undefined;

  const build = (formValues?: Record<string, unknown>) =>
    buildFormData({
      contract: contractState?.contract,
      uuid,
      pathname,
      query: storedQueries,
      formValues,
      formStepId: stepId,
      enums: { products, processes },
    });

  // formLoad: fires once per view (pathname + open step). Opted into by the
  // accordion-level caller passing `visibleForms`.
  useEffect(() => {
    if (visibleForms === undefined) {
      return;
    }
    const key = `${pathname}|${step}`;
    if (lastFormLoadKey === key) {
      return;
    }
    lastFormLoadKey = key;

    const formObject = visibleForms.find(f => f.name === step);
    const formValues = formObject?.form.getValues?.() as FormData["formFields"];

    pushFormLoad(build(formValues));
  }, [pathname, step]);

  // formStart / formUpdate: field-level tracking, enabled when a form is given.
  useFieldTracking(form, build);

  return {
    formStepNext: () => pushFormStepNext(build()),
    formStepPrevious: () => pushFormStepPrevious(build()),
    formSubmitSuccess: () => pushFormSubmitSuccess(build()),
    formError: (errors: FormErrors, values?: Record<string, unknown>) =>
      pushFormError({
        ...build(values),
        formFieldErrors: buildFormFieldErrors(errors),
        errorId: "FE_VALIDATION_ERROR",
        errorTitle: "Chyba validace",
        errorMessage: "Formulář obsahuje chyby",
      }),
  };
};
